import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Search, Plus, X, Download, Eye, Edit2, Check, Loader2, AlertTriangle, Hexagon, Filter, ChevronDown, CreditCard } from 'lucide-react';
import { quotationAPI, customerAPI, productAPI, branchAPI, quotationTemplateAPI, authAPI, salespersonAPI, fetchPaged, proformaInvoiceAPI, companySettingsAPI } from '../../services/api';
import { filterBranchesForUser } from '../../utils/branchScope';
import SignageQuotationBuilder from './SignageQuotationBuilder';
import QuotationForm from './QuotationForm';
import { generateGenericQuotationPDF, generateSignagePDFFromQuote } from './quotationPdfUtils';
import { isSqmMode, sqmLineDisplay } from './signageSqmUtils';
import { SalesDocumentTrail } from '../ui/SalesDocumentTrail';
import RevisionHistory from '../ui/RevisionHistory';
import Pagination from '../ui/Pagination';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import SortableHeader from '../ui/SortableHeader';

const statusColors = {
  Draft: 'bg-gray-100 text-gray-600',
  Sent: 'bg-blue-100 text-blue-700',
  Accepted: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Revised: 'bg-amber-100 text-amber-700',
};

const defaultTerms = {
  payment_terms: '50% advance, 50% against delivery',
  delivery_schedule: 'Within 15 days from PO receipt',
  warranty: '12 months from date of application',
  validity: '30 days from quotation date',
  freight: 'FOR Destination (included in price)',
  notes: 'Rates are exclusive of GST. Material as per IRC/MoRTH specifications.',
};

const QuotationBuilder = () => {
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);  // Sync double-click guard
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showSignage, setShowSignage] = useState(false);
  const [editSignageData, setEditSignageData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editTerms, setEditTerms] = useState(null);
  const [page, setPage] = useState(1);
  const [logoBase64, setLogoBase64] = useState('');
  const [customerShipAddresses, setCustomerShipAddresses] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', order: 'desc' });
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };
  const handleSort = (key, order) => { setSortConfig({ key, order }); setPage(1); };
  const [filterSalesman, setFilterSalesman] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [users, setUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showPIDialog, setShowPIDialog] = useState(false);
  const [piForm, setPIForm] = useState({
    pi_date: new Date().toISOString().slice(0, 10),
    payment_due_date: '',
    bank_details: '',
    notes: '',
    ship_to: { name: '', address: '', city: '', state: '', pincode: '', gstin: '' },
    other_charges: [],
  });
  const [piSubmitting, setPISubmitting] = useState(false);
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');

  // Fetch company bank details for PI form
  useEffect(() => {
    companySettingsAPI.getBankDetails().then(bd => {
      if (bd && (bd.bank_name || bd.account_number)) {
        const lines = [];
        if (bd.account_holder) lines.push(bd.account_holder);
        if (bd.bank_name) lines.push(`Bank: ${bd.bank_name}`);
        if (bd.account_number) lines.push(`A/c No: ${bd.account_number}`);
        if (bd.ifsc_code) lines.push(`IFSC: ${bd.ifsc_code}`);
        if (bd.branch_name) lines.push(`Branch: ${bd.branch_name}`);
        if (bd.upi_id) lines.push(`UPI: ${bd.upi_id}`);
        setPIForm(prev => ({ ...prev, bank_details: prev.bank_details || lines.join('\n') }));
      }
    }).catch(() => {});
  }, []);

  const emptyQuote = {
    customer: '', customer_name: '', customer_gstin: '', customer_address: '', customer_city: '', customer_state: '', customer_phone: '',
    ship_to_name: '', ship_to_address: '', ship_to_city: '', ship_to_state: '', ship_to_pincode: '',
    salesman: user.id || '', salesman_name: user.name || '', salesman_phone: '',
    branch: user.branch || '', project_name: '', project_description: '',
    items: [{ product: '', name: '', sku: '', description: '', hsn_code: '', qty: 0, unit: '', rate: 0, discount: 0, amount: 0, _sell_unit: '', _buy_unit: '', _cost: 0, _margin: 0, _conversions: [], _converted_qty: '' }],
    other_charges: [],
    subtotal: 0, gst_rate: 18, gst_amount: 0, total: 0, terms: { ...defaultTerms }, valid_until: '', status: 'Draft',
  };
  const [form, setForm] = useState({ ...emptyQuote });
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [productSearches, setProductSearches] = useState({});

  // Template state
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (filterSalesman) params.salesman = filterSalesman;
      if (filterBranch) params.branch = filterBranch;
      if (filterCustomer) params.customer = filterCustomer;
      if (filterStatus) params.status = filterStatus;
      if (sortConfig.key) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.order; }
      const [result, customersData, productsData, branchesData, templatesData, usersData, spData] = await Promise.all([
        fetchPaged('/sales/quotations', params),
        customerAPI.getAll().catch(() => []),
        productAPI.getAll().catch(() => []),
        branchAPI.getAll().catch(() => []),
        quotationTemplateAPI.getAll().catch(() => []),
        authAPI.listUsers().catch(() => []),
        salespersonAPI.getAll().catch(() => []),
      ]);
      setQuotes(result.items); setTotal(result.total); setTotalPages(result.total_pages);
      setCustomers(customersData); setProducts(productsData); setBranches(branchesData);
      setTemplates(templatesData); setUsers(usersData); setSalespersons(spData);
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  }, [search, page, pageSize, filterSalesman, filterBranch, filterCustomer, filterStatus, sortConfig]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    fetch('/prismoline-logo.png')
      .then(r => r.blob())
      .then(blob => { const reader = new FileReader(); reader.onloadend = () => setLogoBase64(reader.result); reader.readAsDataURL(blob); })
      .catch(() => {});
  }, []);

  // Smart product selection - auto-fills conversions and pricing
  const selectProduct = (idx, productId) => {
    const prod = products.find(p => p.id === productId);
    if (!prod) return;
    _applyProductToItem(idx, prod);
  };

  const handleQuoteProductSelect = (idx, prod) => {
    _applyProductToItem(idx, prod);
  };

  const _applyProductToItem = (idx, prod) => {
    const items = [...form.items];
    const sellUnit = prod.sell_unit || prod.unit;
    const buyUnit = prod.buy_unit || prod.unit;
    const conversions = prod.unit_conversions || prod.conversions || [];
    items[idx] = {
      ...items[idx], product: prod.id, name: prod.name, unit: sellUnit,
      hsn_code: prod.hsn_code || '', description: items[idx].description || '',
      rate: prod.selling_price || 0, _sell_unit: sellUnit, _buy_unit: buyUnit,
      _cost: prod.cost_price || 0, _conversions: conversions,
      _margin: prod.selling_price > 0 ? Math.round(((prod.selling_price - prod.cost_price) / prod.selling_price) * 100) : 0,
      _converted_qty: '', sku: prod.sku || '',
    };
    items[idx].amount = Math.round(items[idx].qty * items[idx].rate * (1 - items[idx].discount / 100));
    updateConvertedQty(items, idx);
    recalcTotals(items);
    setProductSearches(prev => ({ ...prev, [idx]: undefined }));
  };

  const handleQuoteProductClear = (idx) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], product: '', name: '', unit: '', hsn_code: '', rate: 0, amount: 0, _sell_unit: '', _buy_unit: '', _cost: 0, _conversions: [], _margin: 0, _converted_qty: '' };
    recalcTotals(items);
    setProductSearches(prev => ({ ...prev, [idx]: undefined }));
  };

  const updateConvertedQty = (items, idx) => {
    const item = items[idx];
    if (!item.qty || !item._conversions?.length) return;
    const convTexts = [];
    for (const c of item._conversions) {
      if (c.from_unit === item.unit) {
        convTexts.push(`= ${(item.qty * c.factor).toFixed(1)} ${c.to_unit}`);
      }
    }
    items[idx]._converted_qty = convTexts.join(' | ');
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: ['qty', 'rate', 'discount'].includes(field) ? Number(value) : value };
    if (['qty', 'rate', 'discount'].includes(field)) {
      items[idx].amount = Math.round(items[idx].qty * items[idx].rate * (1 - items[idx].discount / 100));
      updateConvertedQty(items, idx);
    }
    if (field === 'rate' && items[idx]._cost > 0) {
      items[idx]._margin = Math.round(((items[idx].rate - items[idx]._cost) / items[idx].rate) * 100);
    }
    recalcTotals(items);
  };

  const recalcTotals = (items, otherCharges) => {
    const oc = otherCharges || form.other_charges || [];
    const subtotal = items.reduce((s, i) => s + (i.amount || 0), 0);
    const otherTotal = oc.reduce((s, c) => s + (Number(c.amount) || 0), 0);
    const taxableAmount = subtotal + otherTotal;
    const gst_amount = Math.round(taxableAmount * form.gst_rate / 100);
    setForm(prev => ({ ...prev, items, other_charges: oc, subtotal, gst_amount, total: taxableAmount + gst_amount }));
  };

  const addOtherCharge = () => {
    const oc = [...(form.other_charges || []), { description: '', amount: 0 }];
    recalcTotals(form.items, oc);
  };

  const updateOtherCharge = (idx, field, value) => {
    const oc = [...(form.other_charges || [])];
    oc[idx] = { ...oc[idx], [field]: field === 'amount' ? Number(value) : value };
    recalcTotals(form.items, oc);
  };

  const removeOtherCharge = (idx) => {
    const oc = (form.other_charges || []).filter((_, i) => i !== idx);
    recalcTotals(form.items, oc);
  };

  const updateGstRate = (r) => {
    const rate = Number(r);
    const otherTotal = (form.other_charges || []).reduce((s, c) => s + (Number(c.amount) || 0), 0);
    const taxableAmount = form.subtotal + otherTotal;
    const gst_amount = Math.round(taxableAmount * rate / 100);
    setForm(prev => ({ ...prev, gst_rate: rate, gst_amount, total: taxableAmount + gst_amount }));
  };

  const addItem = () => setForm(prev => ({
    ...prev, items: [...prev.items, { product: '', name: '', description: '', hsn_code: '', qty: 0, unit: '', rate: 0, discount: 0, amount: 0, _sell_unit: '', _buy_unit: '', _cost: 0, _margin: 0, _conversions: [], _converted_qty: '' }]
  }));

  const removeItem = (idx) => {
    // If it's the last remaining row, replace with a blank row (instead of blocking delete)
    if (form.items.length <= 1) {
      const blank = { product: '', name: '', sku: '', description: '', hsn_code: '', qty: 0, unit: '', rate: 0, discount: 0, amount: 0, _sell_unit: '', _buy_unit: '', _cost: 0, _margin: 0, _conversions: [], _converted_qty: '' };
      recalcTotals([blank]);
      return;
    }
    const items = form.items.filter((_, i) => i !== idx);
    recalcTotals(items);
  };

  const selectCustomer = (customerId) => {
    const c = customers.find(x => x.id === customerId);
    if (c) {
      setForm(prev => ({
        ...prev, customer: c.id, customer_name: c.name,
        customer_gstin: c.gstin || '', customer_address: c.address || '',
        customer_city: c.city || '', customer_state: c.state || '',
        customer_phone: c.phone || '',
      }));
      setCustomerSearch(c.name);
      setShowCustomerDropdown(false);
    }
  };

  const filteredCustomers = customerSearch
    ? customers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()))
    : customers;

  const handleCreate = async (saveStatus) => {
    if (submittingRef.current) return; // Sync guard: prevents double-click duplicates
    const status = saveStatus || form.status || 'Draft';
    // Silently strip blank rows (no product/name and no qty/rate)
    const nonBlankItems = form.items.filter(i => (i.name && i.name.trim()) || i.product || Number(i.qty) > 0 || Number(i.rate) > 0);
    if (status !== 'Draft' && (!form.customer || !form.valid_until || nonBlankItems.length === 0 || nonBlankItems.some(i => !i.name || !Number(i.qty)))) {
      alert('Please select customer, set validity date, and add items'); return;
    }
    if (!form.customer && status === 'Draft' && nonBlankItems.length === 0) {
      alert('Please add at least a customer or items to save as draft'); return;
    }
    submittingRef.current = true;
    try {
      setSubmitting(true);
      const cleanItems = nonBlankItems.map(({ _sell_unit, _buy_unit, _cost, _margin, _conversions, _converted_qty, ...rest }) => rest);
      const payload = { ...form, items: cleanItems, status };
      if (editingQuoteId) {
        await quotationAPI.update(editingQuoteId, payload);
      } else {
        await quotationAPI.create(payload);
      }
      setShowCreate(false); setForm({ ...emptyQuote }); setEditingQuoteId(null); fetchAll();
    } catch (err) {
      console.error('Save error:', err);
      alert(err.response?.data?.detail || 'Failed to save quotation');
    } finally { setSubmitting(false); submittingRef.current = false; }
  };

  const handleEditQuote = (quote) => {
    if (quote.quotation_type === 'signage') {
      setEditSignageData(quote); setShowSignage(true);
      return;
    }
    const items = (quote.items || []).map(item => ({
      product: item.product || '', name: item.name || '', description: item.description || '',
      hsn_code: item.hsn_code || '', qty: item.qty || 0, unit: item.unit || '',
      rate: item.rate || 0, discount: item.discount || 0, amount: item.amount || 0,
      _sell_unit: '', _buy_unit: '', _cost: 0, _margin: 0, _conversions: [], _converted_qty: '',
    }));
    setForm({
      customer: quote.customer || '', customer_name: quote.customer_name || '',
      customer_gstin: quote.customer_gstin || '', customer_address: quote.customer_address || '',
      customer_city: quote.customer_city || '', customer_state: quote.customer_state || '',
      customer_phone: quote.customer_phone || '',
      ship_to_name: quote.ship_to_name || '', ship_to_address: quote.ship_to_address || '',
      ship_to_city: quote.ship_to_city || '', ship_to_state: quote.ship_to_state || '',
      ship_to_pincode: quote.ship_to_pincode || '',
      salesman: quote.salesman || user.id || '', salesman_name: quote.salesman_name || user.name || '',
      salesman_phone: quote.salesman_phone || '',
      branch: quote.branch || '', project_name: quote.project_name || '', project_description: quote.project_description || '',
      items, other_charges: quote.other_charges || [],
      subtotal: quote.subtotal || 0, gst_rate: quote.gst_rate || 18,
      gst_amount: quote.gst_amount || 0, total: quote.total || 0,
      terms: quote.terms || { ...defaultTerms }, valid_until: quote.valid_until || '', status: quote.status || 'Draft',
    });
    setCustomerSearch(quote.customer_name || '');
    setEditingQuoteId(quote.id);
    setShowCreate(true);
    // Fetch shipping addresses for this customer
    if (quote.customer) {
      customerAPI.getShippingAddresses(quote.customer).then(addrs => setCustomerShipAddresses(Array.isArray(addrs) ? addrs : [])).catch(() => setCustomerShipAddresses([]));
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try { await quotationAPI.updateStatus(id, status); fetchAll(); }
    catch (err) { alert('Failed to update status'); }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) { alert('Enter template name'); return; }
    try {
      const cleanItems = form.items.map(({ _sell_unit, _buy_unit, _cost, _margin, _conversions, _converted_qty, ...rest }) => rest);
      await quotationTemplateAPI.create({
        name: templateName,
        items: cleanItems,
        terms: form.terms,
        gst_rate: form.gst_rate,
      });
      alert('Template saved!');
      setShowSaveTemplate(false);
      setTemplateName('');
      const t = await quotationTemplateAPI.getAll().catch(() => []);
      setTemplates(t);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save template'); }
  };

  const loadTemplate = (template) => {
    setForm(prev => ({
      ...prev,
      items: template.items.map(item => ({
        product: item.product || '', name: item.name || '', qty: item.qty || 0, unit: item.unit || '',
        rate: item.rate || 0, discount: item.discount || 0, amount: item.amount || 0,
        _sell_unit: '', _buy_unit: '', _cost: 0, _margin: 0, _conversions: [], _converted_qty: '',
      })),
      terms: template.terms || { ...defaultTerms },
      gst_rate: template.gst_rate || 18,
    }));
    // Recalculate totals
    const subtotal = template.items.reduce((s, i) => s + (i.amount || 0), 0);
    const gst_amount = Math.round(subtotal * (template.gst_rate || 18) / 100);
    setForm(prev => ({ ...prev, subtotal, gst_amount, total: subtotal + gst_amount }));
    setShowTemplates(false);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await quotationTemplateAPI.delete(id); setTemplates(templates.filter(t => t.id !== id)); }
    catch (err) { alert('Failed to delete template'); }
  };

  const handleCreatePIFromQuotation = async () => {
    if (!preview?.id) return;
    setPISubmitting(true);
    try {
      const result = await proformaInvoiceAPI.createFromQuotation({
        quotation_id: preview.id,
        ...piForm,
      });
      toast.success(`Proforma Invoice ${result.pi_number} generated from Quotation`);
      setShowPIDialog(false);
      setPIForm(prev => ({ ...prev, payment_due_date: '', notes: '' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create Proforma Invoice');
    }
    setPISubmitting(false);
  };

  // Calculate total material requirement for the quotation
  const getMaterialBreakdown = () => {
    const materials = {};
    for (const item of form.items) {
      if (!item.name || !item.qty) continue;
      const key = `${item.name} (${item.unit})`;
      if (!materials[key]) materials[key] = { name: item.name, sell_qty: 0, sell_unit: item.unit, buy_qty: null, buy_unit: item._buy_unit, conversions: [] };
      materials[key].sell_qty += item.qty;
      // Calculate buy quantity from conversions
      if (item._conversions?.length && item._buy_unit && item._buy_unit !== item.unit) {
        for (const c of item._conversions) {
          if (c.from_unit === item.unit && c.to_unit === item._buy_unit) {
            materials[key].buy_qty = (materials[key].buy_qty || 0) + item.qty * c.factor;
            break;
          }
        }
      }
      // Add all conversion info
      for (const c of (item._conversions || [])) {
        if (c.from_unit === item.unit) {
          materials[key].conversions.push({ qty: (item.qty * c.factor).toFixed(1), unit: c.to_unit });
        }
      }
    }
    return Object.values(materials);
  };

  const generatePDF = (quote) => {
    const q = { ...quote, _branches: branches };
    if (q.quotation_type === 'signage' && q.signage_items?.length > 0) {
      return generateSignagePDFFromQuote(q, branches, logoBase64);
    }
    return generateGenericQuotationPDF(q, logoBase64);
  };

  const filtered = quotes;

  const materialBreakdown = showCreate ? getMaterialBreakdown() : [];

  // ===== CREATE / SMART BUILDER VIEW =====
  if (showSignage) {
    return <SignageQuotationBuilder onBack={() => { setShowSignage(false); setEditSignageData(null); fetchAll(); }} editQuotation={editSignageData} />;
  }
  if (showCreate) {
    return (
      <QuotationForm
        form={form} setForm={setForm} emptyQuote={emptyQuote}
        editingQuoteId={editingQuoteId} setEditingQuoteId={setEditingQuoteId}
        customerSearch={customerSearch} setCustomerSearch={setCustomerSearch}
        submitting={submitting}
        templates={templates} showTemplates={showTemplates} setShowTemplates={setShowTemplates}
        showSaveTemplate={showSaveTemplate} setShowSaveTemplate={setShowSaveTemplate}
        templateName={templateName} setTemplateName={setTemplateName}
        materialBreakdown={materialBreakdown} users={users} branches={branches}
        salespersons={salespersons} customers={customers} products={products}
        customerShipAddresses={customerShipAddresses}
        updateItem={updateItem} addItem={addItem} removeItem={removeItem}
        handleQuoteProductSelect={handleQuoteProductSelect}
        handleQuoteProductClear={handleQuoteProductClear}
        updateGstRate={updateGstRate}
        addOtherCharge={addOtherCharge} updateOtherCharge={updateOtherCharge} removeOtherCharge={removeOtherCharge}
        handleCreate={handleCreate} handleSaveTemplate={handleSaveTemplate}
        loadTemplate={loadTemplate} deleteTemplate={deleteTemplate}
        onClose={() => setShowCreate(false)}
        onCustomerChange={async (customerId) => {
          if (!customerId) { setCustomerShipAddresses([]); return; }
          try {
            const addrs = await customerAPI.getShippingAddresses(customerId);
            setCustomerShipAddresses(Array.isArray(addrs) ? addrs : []);
          } catch { setCustomerShipAddresses([]); }
        }}
      />
    );
  }

  // ===== LIST VIEW =====
  const activeFilterCount = [filterSalesman, filterBranch, filterCustomer, filterStatus].filter(Boolean).length;

  return (
    <div data-testid="quotation-list">
      <div className="flex flex-col gap-3 mb-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input data-testid="quote-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by quote #, customer..." className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
          </div>
          <button data-testid="toggle-filters" onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-all ${showFilters || activeFilterCount > 0 ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <Filter className="w-3.5 h-3.5" />
            Filters {activeFilterCount > 0 && <span className="bg-blue-600 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">{activeFilterCount}</span>}
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <button data-testid="new-signage-quote-btn" onClick={() => { setEditSignageData(null); setShowSignage(true); }} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200" style={{ backgroundColor: '#1a3d7c' }}>
              <Hexagon className="w-3.5 h-3.5" /> Signage Quotation
            </button>
            <button data-testid="new-quote-btn" onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200" style={{ backgroundColor: '#24549c' }}>
              <Plus className="w-3.5 h-3.5" /> Generic Quotation
            </button>
          </div>
        </div>

        {/* Filter Row */}
        {showFilters && (
          <div data-testid="filter-row" className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <label className="block text-[9px] font-semibold text-gray-500 mb-0.5 uppercase tracking-wide">Salesman</label>
              <select data-testid="filter-salesman" value={filterSalesman} onChange={e => { setFilterSalesman(e.target.value); setPage(1); }}
                className="w-44 px-2.5 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-blue-200">
                <option value="">All Salesmen</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[9px] font-semibold text-gray-500 mb-0.5 uppercase tracking-wide">Branch</label>
              <select data-testid="filter-branch" value={filterBranch} onChange={e => { setFilterBranch(e.target.value); setPage(1); }}
                className="w-44 px-2.5 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-blue-200">
                <option value="">All Branches</option>
                {filterBranchesForUser(branches).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[9px] font-semibold text-gray-500 mb-0.5 uppercase tracking-wide">Customer</label>
              <select data-testid="filter-customer" value={filterCustomer} onChange={e => { setFilterCustomer(e.target.value); setPage(1); }}
                className="w-44 px-2.5 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-blue-200">
                <option value="">All Customers</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[9px] font-semibold text-gray-500 mb-0.5 uppercase tracking-wide">Status</label>
              <select data-testid="filter-status" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                className="w-36 px-2.5 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-blue-200">
                <option value="">All Statuses</option>
                {['Draft', 'Sent', 'Accepted', 'Rejected', 'Revised'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {activeFilterCount > 0 && (
              <button data-testid="clear-filters" onClick={() => { setFilterSalesman(''); setFilterBranch(''); setFilterCustomer(''); setFilterStatus(''); setPage(1); }}
                className="mt-4 text-[10px] font-semibold text-red-500 hover:text-red-700 flex items-center gap-1">
                <X className="w-3 h-3" /> Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /><span className="ml-2 text-sm text-gray-500">Loading...</span></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No quotations yet</p>
          <p className="text-xs text-gray-400 mt-1">Create your first smart quotation</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-gray-100 bg-gray-50">
                <SortableHeader label="Quote #" sortKey="quotation_number" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Type" />
                <SortableHeader label="Customer" />
                <SortableHeader label="Salesman" />
                <SortableHeader label="Date" sortKey="created_at" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Items" />
                <SortableHeader label="Total" />
                <SortableHeader label="Status" />
                <SortableHeader label="Actions" />
              </tr></thead>
              <tbody>
                {filtered.map(q => (
                  <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      <div className="text-xs font-semibold text-gray-900">{q.quotation_number || q.id?.substring(0, 12)}</div>
                      {q.revision > 1 && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">Rev-{q.revision}</span>}
                    </td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${q.quotation_type === 'signage' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>{q.quotation_type === 'signage' ? 'Signage' : 'Generic'}</span></td>
                    <td className="px-4 py-3 text-xs font-medium text-gray-900">{q.customer_name}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{q.salesman_name || '-'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{q.date}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{q.quotation_type === 'signage' ? ((q.signage_items?.length || 0) + (q.items?.length || 0)) : (q.items?.length || 0)}</td>
                    <td className="px-4 py-3 text-xs font-bold" style={{ color: '#24549c' }}>₹{(q.total || 0).toLocaleString()}</td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColors[q.status] || 'bg-gray-100'}`}>{q.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setPreview(q)} className="p-1 rounded hover:bg-gray-100" title="Preview"><Eye className="w-3.5 h-3.5 text-gray-400" /></button>
                        <button onClick={() => generatePDF(q)} className="p-1 rounded hover:bg-gray-100" title="PDF"><Download className="w-3.5 h-3.5 text-gray-400" /></button>
                        {q.quotation_type === 'signage' && q.status !== 'Revised' && (
                          <button data-testid={`edit-signage-${q.id}`} onClick={() => { setEditSignageData(q); setShowSignage(true); }} className="p-1 rounded hover:bg-blue-50" title="Edit"><Edit2 className="w-3.5 h-3.5 text-blue-500" /></button>
                        )}
                        {q.quotation_type !== 'signage' && (q.status === 'Draft' || q.status === 'Sent') && (
                          <button data-testid={`edit-generic-${q.id}`} onClick={() => handleEditQuote(q)} className="p-1 rounded hover:bg-blue-50" title="Edit"><Edit2 className="w-3.5 h-3.5 text-blue-500" /></button>
                        )}
                        {q.status === 'Draft' && (
                          <select value={q.status} onChange={e => handleStatusUpdate(q.id, e.target.value)} className="text-[10px] border border-gray-200 rounded px-1 py-0.5">
                            <option value="Draft">Draft</option><option value="Sent">Sent</option><option value="Accepted">Accepted</option><option value="Rejected">Rejected</option>
                          </select>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t border-gray-100 text-[10px] text-gray-500">
            {total} quotations | Total value: ₹{filtered.reduce((s, q) => s + (q.total || 0), 0).toLocaleString()}
          </div>
        </div>
      )}

      {quotes.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>Quotation Preview</h2>
              <div className="flex items-center gap-2">
                <button data-testid="quotation-to-pi-btn" onClick={() => {
                  // Pre-fill PI form from the preview quotation
                  setPIForm(prev => ({
                    ...prev,
                    ship_to: {
                      name: preview?.ship_to_name || '',
                      address: preview?.ship_to_address || '',
                      city: preview?.ship_to_city || '',
                      state: preview?.ship_to_state || '',
                      pincode: '',
                      gstin: '',
                    },
                    other_charges: (preview?.other_charges || []).map(c => ({
                      description: c.description || '',
                      amount: Number(c.amount) || 0,
                      taxable: c.taxable !== false,
                    })),
                  }));
                  setShowPIDialog(true);
                }} className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg bg-emerald-600 hover:bg-emerald-700"><CreditCard className="w-3 h-3" /> Convert to PI</button>
                <button onClick={() => generatePDF(preview)} className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}><Download className="w-3 h-3" /> PDF</button>
                <button onClick={() => setPreview(null)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
              </div>
            </div>
            <div className="p-5">
              {/* Document Trail */}
              <SalesDocumentTrail docType="quotation" docId={preview.id} />
              <div className="flex justify-between mb-4 mt-4">
                <div><h3 className="text-lg font-bold" style={{ color: '#24549c', fontFamily: 'DM Sans' }}>PRISMOLINE</h3><p className="text-[10px] text-gray-400">Sharda Infrasolutions Private Limited</p></div>
                <div className="text-right text-xs">
                  <p className="font-mono font-bold">{preview.quotation_number || preview.id?.substring(0, 16)}</p>
                  {preview.revision > 1 && <p className="text-amber-600 font-semibold">Revision {preview.revision}</p>}
                  <p className="text-gray-500">Date: {preview.date}</p>
                  <p className="text-gray-500">Valid: {preview.valid_until}</p>
                </div>
              </div>
              <div className="mb-4 text-xs grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500">To:</p>
                  <p className="font-bold text-gray-900">{preview.customer_name}</p>
                  {(preview.customer_gstin || preview.customer_city || preview.customer_phone) && (
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {preview.customer_gstin && <p>GSTIN: {preview.customer_gstin}</p>}
                      {(preview.customer_address || preview.customer_city) && <p>{[preview.customer_address, preview.customer_city, preview.customer_state].filter(Boolean).join(', ')}</p>}
                      {preview.customer_phone && <p>Ph: {preview.customer_phone}</p>}
                    </div>
                  )}
                </div>
                <div><p className="text-gray-500">Salesman:</p><p className="font-bold text-gray-900">{preview.salesman_name || '-'}</p></div>
              </div>
              {preview.signage_project && (() => {
                const sp = preview.signage_project;
                const siItems = preview.signage_items || [];
                const wts = siItems.map(si => si.work_type || sp.work_type || '').filter(Boolean);
                const uniqueWT = Array.from(new Set(wts));
                const mixedWT = uniqueWT.length > 1;
                const headerWT = mixedWT ? '' : (uniqueWT[0] || sp.work_type || '');
                return (
                <div className="mb-4 p-3 bg-indigo-50 rounded-lg text-xs">
                  <p className="font-bold text-indigo-700 mb-1">Signage Project</p>
                  {sp.project_name && <p className="text-indigo-600">Project: {sp.project_name}</p>}
                  {sp.stretch_no && <p className="text-indigo-600">Stretch: {sp.stretch_no}</p>}
                  {sp.work_for && <p className="text-indigo-600">Work: {sp.work_for}{headerWT ? ` - ${headerWT}` : ''}{mixedWT && <span className="ml-2 text-[10px] text-indigo-400">(mixed types — see per row)</span>}</p>}
                </div>
                );
              })()}
              {preview.quotation_type === 'signage' && preview.signage_items?.length > 0 ? (
                <table className="w-full mb-4 text-xs">
                  <thead><tr className="text-white text-[10px]" style={{ backgroundColor: '#24549c' }}>
                    {['#', 'Description', 'Shape', 'Dimensions', 'Qty', 'Rate', 'Amount'].map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)}
                  </tr></thead>
                  <tbody>{(preview.signage_items || []).map((item, i) => {
                    const isPcs = item.pricing_mode === 'per_pcs';
                    const sp = preview.signage_project || {};
                    const siItems = preview.signage_items || [];
                    const wts = siItems.map(si => si.work_type || sp.work_type || '').filter(Boolean);
                    const mixed = Array.from(new Set(wts)).length > 1;
                    const rowWT = item.work_type || sp.work_type || '';
                    const rate = isPcs ? (item.custom_per_pcs_rate || item.final_per_board) : item.final_per_board;
                    const _sqm = isSqmMode(item) ? sqmLineDisplay(item, item.qty, Number(item.total_amount) || 0) : null;
                    return (
                    <tr key={i} className="border-b border-gray-100 align-top">
                      <td className="px-3 py-2">{i + 1}</td>
                      <td className="px-3 py-2 font-medium">
                        {item.description || '-'}
                        {mixed && rowWT && <span className="block text-[10px] text-indigo-600 font-semibold mt-0.5">[{rowWT}]</span>}
                        {item.specific_description && <span className="block text-[10px] text-gray-500 font-normal mt-0.5">Description: {item.specific_description}</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{item.shape || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{item.dimensions || '-'}</td>
                      <td className="px-3 py-2 font-semibold">{_sqm ? `${_sqm.totalSqm} SQM` : (item.qty || 0)}</td>
                      <td className="px-3 py-2">₹{(_sqm ? _sqm.ratePerSqm : (rate || 0)).toLocaleString()}</td>
                      <td className="px-3 py-2 font-semibold">₹{(item.total_amount || 0).toLocaleString()}</td>
                    </tr>
                    );
                  })}</tbody>
                </table>
              ) : (
              <table className="w-full mb-4 text-xs">
                <thead><tr className="text-white text-[10px]" style={{ backgroundColor: '#24549c' }}>{['#', 'Product', 'HSN', 'Qty', 'Unit', 'Rate', 'Disc', 'Amount'].map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
                <tbody>{(preview.items || []).map((item, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-3 py-2">{i + 1}</td>
                    <td className="px-3 py-2">
                      <span className="font-medium">{item.name}</span>
                      {item.description && <span className="block text-[10px] text-gray-400">{item.description}</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-500">{item.hsn_code || '-'}</td>
                    <td className="px-3 py-2">{item.qty}</td><td className="px-3 py-2">{item.unit}</td>
                    <td className="px-3 py-2">₹{(item.rate || 0).toLocaleString()}</td><td className="px-3 py-2">{item.discount}%</td>
                    <td className="px-3 py-2 font-semibold">₹{(item.amount || 0).toLocaleString()}</td>
                  </tr>
                ))}</tbody>
              </table>
              )}
              {/* Additional (normal) products in a signage quotation */}
              {preview.quotation_type === 'signage' && (preview.items || []).length > 0 && (
                <div className="mb-4">
                  <h4 className="text-[11px] font-bold text-gray-700 mb-1">Additional Products</h4>
                  <table className="w-full text-xs">
                    <thead><tr className="text-white text-[10px]" style={{ backgroundColor: '#3a6cb5' }}>{['#', 'Product', 'HSN', 'Qty', 'Unit', 'Rate', 'Amount'].map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
                    <tbody>{(preview.items || []).map((item, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="px-3 py-2">{(preview.signage_items || []).length + i + 1}</td>
                        <td className="px-3 py-2"><span className="font-medium">{item.name}</span>{item.description && <span className="block text-[10px] text-gray-400">{item.description}</span>}</td>
                        <td className="px-3 py-2 font-mono text-gray-500">{item.hsn_code || '-'}</td>
                        <td className="px-3 py-2">{item.qty}</td><td className="px-3 py-2">{item.unit}</td>
                        <td className="px-3 py-2">₹{(item.rate || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 font-semibold">₹{(item.amount || 0).toLocaleString()}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
              <div className="flex justify-end mb-4">
                <div className="w-56 text-xs space-y-1">
                  <div className="flex justify-between"><span>Subtotal</span><span>₹{(preview.subtotal || 0).toLocaleString()}</span></div>
                  {(preview.other_charges || []).filter(c => c.amount > 0).map((c, i) => (
                    <div key={i} className="flex justify-between text-gray-400"><span>{c.description || 'Other'}</span><span>₹{Number(c.amount).toLocaleString()}</span></div>
                  ))}
                  <div className="flex justify-between"><span>GST ({preview.gst_rate || 0}%)</span><span>₹{(preview.gst_amount || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between font-bold text-sm pt-1 border-t" style={{ color: '#24549c' }}><span>Total</span><span>₹{(preview.total || 0).toLocaleString()}</span></div>
                </div>
              </div>
              {preview.terms && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-xs font-bold text-gray-900 mb-2">Terms & Conditions</h4>
                  <div className="space-y-1 text-[11px] text-gray-600">
                    {Object.entries(preview.terms).map(([key, val]) => val ? (
                      <p key={key}><strong>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:</strong> {val}</p>
                    ) : null)}
                  </div>
                </div>
              )}
              {/* Revision History */}
              <RevisionHistory docType="quotation" docId={preview.id} compact />
            </div>
          </div>
        </div>
      )}

      {/* Proforma Invoice from Quotation Dialog */}
      <Dialog open={showPIDialog} onOpenChange={setShowPIDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quotation-pi-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-emerald-600" /> Generate Proforma Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded-lg text-xs space-y-1">
              <p><span className="text-gray-500">Quotation:</span> <span className="font-semibold">{preview?.quotation_number}</span></p>
              <p><span className="text-gray-500">Customer:</span> <span className="font-semibold">{preview?.customer_name}</span></p>
              <p><span className="text-gray-500">Total:</span> <span className="font-bold" style={{ color: '#24549c' }}>₹{(preview?.total || 0).toLocaleString()}</span></p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">PI Date</label>
                <Input type="date" value={piForm.pi_date} onChange={e => setPIForm(f => ({ ...f, pi_date: e.target.value }))} data-testid="pi-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Payment Due Date</label>
                <Input type="date" value={piForm.payment_due_date} onChange={e => setPIForm(f => ({ ...f, payment_due_date: e.target.value }))} data-testid="pi-due-date" />
              </div>
            </div>

            {/* Ship-To Address — pre-filled from quotation, editable per-PI */}
            <div className="border border-gray-200 rounded-lg p-3 bg-blue-50/30" data-testid="pi-ship-to-section">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Ship To (Delivery Address)</label>
                <span className="text-[10px] text-gray-500">{preview?.ship_to_address ? 'Pre-filled from Quotation — edit as needed' : 'Optional'}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="pi-ship-to-name" placeholder="Site / Recipient name"
                  value={piForm.ship_to.name}
                  onChange={e => setPIForm(f => ({ ...f, ship_to: { ...f.ship_to, name: e.target.value } }))} className="text-xs h-8" />
                <Input data-testid="pi-ship-to-gstin" placeholder="GSTIN (optional)"
                  value={piForm.ship_to.gstin}
                  onChange={e => setPIForm(f => ({ ...f, ship_to: { ...f.ship_to, gstin: e.target.value } }))} className="text-xs h-8" />
              </div>
              <Input data-testid="pi-ship-to-address" placeholder="Address"
                value={piForm.ship_to.address}
                onChange={e => setPIForm(f => ({ ...f, ship_to: { ...f.ship_to, address: e.target.value } }))} className="text-xs h-8 mt-2" />
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input data-testid="pi-ship-to-city" placeholder="City"
                  value={piForm.ship_to.city}
                  onChange={e => setPIForm(f => ({ ...f, ship_to: { ...f.ship_to, city: e.target.value } }))} className="text-xs h-8" />
                <Input data-testid="pi-ship-to-state" placeholder="State"
                  value={piForm.ship_to.state}
                  onChange={e => setPIForm(f => ({ ...f, ship_to: { ...f.ship_to, state: e.target.value } }))} className="text-xs h-8" />
                <Input data-testid="pi-ship-to-pincode" placeholder="Pincode"
                  value={piForm.ship_to.pincode}
                  onChange={e => setPIForm(f => ({ ...f, ship_to: { ...f.ship_to, pincode: e.target.value } }))} className="text-xs h-8" />
              </div>
            </div>

            {/* Other Charges (freight, etc.) — pre-filled, editable */}
            <div className="border border-gray-200 rounded-lg p-3 bg-amber-50/30" data-testid="pi-other-charges-section">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Other Charges (Freight, Packing, etc.)</label>
                <button type="button" data-testid="pi-add-charge"
                  onClick={() => setPIForm(f => ({ ...f, other_charges: [...f.other_charges, { description: '', amount: 0, taxable: true }] }))}
                  className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900">+ Add</button>
              </div>
              {piForm.other_charges.length === 0 ? (
                <p className="text-[11px] text-gray-400 italic">No additional charges</p>
              ) : (
                <div className="space-y-1">
                  {piForm.other_charges.map((ch, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input data-testid={`pi-charge-desc-${idx}`} placeholder="e.g., Freight"
                        value={ch.description}
                        onChange={e => setPIForm(f => {
                          const cs = [...f.other_charges]; cs[idx] = { ...cs[idx], description: e.target.value }; return { ...f, other_charges: cs };
                        })} className="text-xs h-8 flex-1" />
                      <Input data-testid={`pi-charge-amount-${idx}`} type="number" placeholder="Amount"
                        value={ch.amount}
                        onChange={e => setPIForm(f => {
                          const cs = [...f.other_charges]; cs[idx] = { ...cs[idx], amount: Number(e.target.value) || 0 }; return { ...f, other_charges: cs };
                        })} className="text-xs h-8 w-28 text-right" />
                      <label className="flex items-center gap-1 text-[10px] text-gray-600 cursor-pointer">
                        <input type="checkbox" data-testid={`pi-charge-taxable-${idx}`}
                          checked={ch.taxable !== false}
                          onChange={e => setPIForm(f => {
                            const cs = [...f.other_charges]; cs[idx] = { ...cs[idx], taxable: e.target.checked }; return { ...f, other_charges: cs };
                          })} className="w-3 h-3" />
                        +GST
                      </label>
                      <button type="button" data-testid={`pi-charge-remove-${idx}`}
                        onClick={() => setPIForm(f => ({ ...f, other_charges: f.other_charges.filter((_, i) => i !== idx) }))}
                        className="text-red-400 hover:text-red-600 text-sm px-1">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Bank Details</label>
              <Textarea rows={4} value={piForm.bank_details} onChange={e => setPIForm(f => ({ ...f, bank_details: e.target.value }))} data-testid="pi-bank-details" placeholder="Bank name, account number, IFSC..." />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
              <Textarea rows={2} value={piForm.notes} onChange={e => setPIForm(f => ({ ...f, notes: e.target.value }))} data-testid="pi-notes" placeholder="Additional notes for the PI..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPIDialog(false)}>Cancel</Button>
            <Button onClick={handleCreatePIFromQuotation} disabled={piSubmitting} data-testid="pi-submit" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
              {piSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
              Generate PI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotationBuilder;
